import { Icon } from "@iconify/react";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import type { InsuranceClaim } from "@/shared/types";
import { useState } from "react";
import { cn } from "@/shared/lib/utils";

interface ClaimsSectionProps {
  claims: InsuranceClaim[];
}

const statusConfig: Record<string, { icon: string; label: string }> = {
  pending:  { icon: "lucide:clock",         label: "Pending"  },
  approved: { icon: "lucide:check-circle-2", label: "Approved" },
  denied:   { icon: "lucide:x-circle",      label: "Denied"   },
  partial:  { icon: "lucide:alert-circle",  label: "Partial"  },
  appeal:   { icon: "lucide:file-text",     label: "Appeal"   },
};

const statusPillStyles: Record<string, string> = {
  pending:  "bg-[#F3F4F6] text-[#6B7280]",
  approved: "bg-[#F3F4F6] text-[#6B7280]",
  denied:   "bg-[#F3F4F6] text-[#6B7280]",
  partial:  "bg-[#F3F4F6] text-[#6B7280]",
  appeal:   "bg-[#F3F4F6] text-[#6B7280]",
};

function formatDate(dateString: string): string {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const date = new Date(dateString + "T00:00:00");
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 2,
  }).format(amount);
}

// Same RedIcon as Diagnosis + Medications sections
function RedIcon({ icon, size = "md" }: { icon: string; size?: "sm" | "md" | "lg" }) {
  return (
    <div className={cn(
      "flex shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50",
      size === "sm" && "h-7 w-7",
      size === "md" && "h-9 w-9",
      size === "lg" && "h-11 w-11"
    )}>
      <Icon icon={icon} className={cn(
        "!text-white",
        size === "sm" && "h-3 w-3",
        size === "md" && "h-4 w-4",
        size === "lg" && "h-5 w-5"
      )} />
    </div>
  );
}

export function ClaimsSection({ claims }: ClaimsSectionProps) {
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const filteredClaims = filterStatus
    ? claims.filter(c => c.status === filterStatus)
    : claims;

  const sortedClaims = [...filteredClaims].sort(
    (a, b) => new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime()
  );

  const totalBilled = claims.reduce((sum, c) => sum + c.billedAmount, 0);
  const totalPaid   = claims.reduce((sum, c) => sum + c.paidAmount, 0);
  const totalOwed   = claims.reduce((sum, c) => sum + c.patientResponsibility, 0);

  // No claims: one full-height empty card, no $0.00 stat cards around it.
  if (claims.length === 0) {
    return (
      <div className="flex min-h-full flex-col bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <EmptyState
          icon={<Icon icon="lucide:receipt" className="h-8 w-8 text-[#9CA3AF]" />}
          title="No claims yet"
          description="Your insurance claims will show up here."
          className="min-h-[400px] flex-1 rounded-3xl"
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">

      {/* Stats cards — minimal dashboard style */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-4">
        {[
          { label: "Total Claims",    value: claims.length              },
          { label: "Total Billed",    value: formatCurrency(totalBilled) },
          { label: "Insurance Paid",  value: formatCurrency(totalPaid)   },
          { label: "You Owe",         value: formatCurrency(totalOwed)   },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="flex cursor-default flex-col justify-between min-h-[110px] min-w-0 rounded-3xl border border-[#E5E7EB] bg-white px-4 py-5 shadow-sm transition-all sm:px-6 hover:-translate-y-0.5 hover:shadow-lg"
          >
            <p className="text-sm font-normal text-[#9CA3AF]">{label}</p>
            <div className="flex justify-end">
              <p className="text-2xl font-bold text-[#111827]">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter buttons */}
      <div className="mb-5 flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => setFilterStatus(null)}
          className={cn(
            "!h-auto !rounded-lg !px-3 !py-1.5 !text-xs !font-semibold",
            filterStatus === null
              ? "!border-[#0A2947] !bg-[#0A2947] !text-white hover:!border-[#0A2947] hover:!bg-[#0A2947] hover:!text-white"
              : "!border-[#E5E7EB] !text-[#4B5563] hover:!border-[#0A2947] hover:!text-[#0A2947]"
          )}
        >
          All ({claims.length})
        </Button>
        {Object.entries(statusConfig).map(([status, config]) => {
          const count = claims.filter(c => c.status === status).length;
          if (count === 0) return null;
          return (
            <Button
              key={status}
              variant="outline"
              onClick={() => setFilterStatus(status)}
              className={cn(
                "!h-auto !rounded-lg !px-3 !py-1.5 !text-xs !font-semibold",
                filterStatus === status
                  ? "!border-[#0A2947] !bg-[#0A2947] !text-white hover:!border-[#0A2947] hover:!bg-[#0A2947] hover:!text-white"
                  : "!border-[#E5E7EB] !text-[#4B5563] hover:!border-[#0A2947] hover:!text-[#0A2947]"
              )}
            >
              {config.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Claims list */}
      <div className="space-y-3">
        {sortedClaims.map((claim) => {
          const status = statusConfig[claim.status];
          return (
            <div
              key={claim.id}
              className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all hover:border-[#F36F8F]/50 hover:shadow-md"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                {/* Left — icon + info */}
                <div className="flex min-w-0 flex-1 items-start gap-4">
                  <RedIcon icon="lucide:receipt" size="md" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-extrabold text-[#0A2947]">{claim.serviceType}</h3>
                      <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", statusPillStyles[claim.status])}>
                        {status.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-[#4B5563]">{claim.providerName}</p>
                    <div className="mt-1.5 flex flex-wrap gap-4 text-xs text-[#9CA3AF]">
                      <span className="flex items-center gap-1">
                        <Icon icon="lucide:calendar" className="h-3 w-3" />
                        {formatDate(claim.serviceDate)}
                      </span>
                      <span>Claim #: {claim.claimNumber}</span>
                    </div>
                    {claim.notes && (
                      <p className="mt-1.5 text-xs text-[#4B5563]">{claim.notes}</p>
                    )}
                  </div>
                </div>

                {/* Right — amounts */}
                <div className="shrink-0 space-y-1 text-right">
                  <div className="flex items-center justify-end gap-6">
                    <div>
                      <p className="text-[10px] text-[#9CA3AF]">Billed</p>
                      <p className="text-sm font-semibold text-[#0A2947]">{formatCurrency(claim.billedAmount)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#9CA3AF]">Insurance Paid</p>
                      <p className="text-sm font-semibold text-[#E4002B]">{formatCurrency(claim.paidAmount)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#9CA3AF]">You Owe</p>
                      <p className="text-sm font-extrabold text-[#0A2947]">{formatCurrency(claim.patientResponsibility)}</p>
                    </div>
                  </div>
                  {claim.allowedAmount !== claim.billedAmount && (
                    <p className="text-[10px] text-[#9CA3AF]">
                      Allowed: {formatCurrency(claim.allowedAmount)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Understanding claims info box */}
      <div className="mt-5 rounded-3xl border border-[#E4002B]/20 bg-[#FFF0F3] p-5">
        <h3 className="mb-3 text-sm font-extrabold text-[#E4002B]">Understanding Your Claims</h3>
        <div className="space-y-1.5 text-xs text-[#4B5563]">
          <p><span className="font-extrabold text-[#0A2947]">Billed Amount:</span> What the provider charged for the service.</p>
          <p><span className="font-extrabold text-[#0A2947]">Allowed Amount:</span> The maximum your insurance agrees to pay for this service.</p>
          <p><span className="font-extrabold text-[#0A2947]">Insurance Paid:</span> What your insurance company paid to the provider.</p>
          <p><span className="font-extrabold text-[#0A2947]">You Owe:</span> Your responsibility, including copays, deductible, and coinsurance.</p>
        </div>
      </div>
    </div>
  );
}